import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { apiFetch, apiJson } from '../lib/api'
import { Failed, PendingButton, SkeletonRows } from '../components/pending'
import { PageHeader, PageBody } from '../components/PageHeader'
import { PAY_TABS, PairTabs } from '../components/PairTabs'
import { setOpenQueries } from '../lib/openQueries'
import styles from './QueriesPage.module.css'

// A picker's queries about a day's work, raised in Dataphyll Crew ("Query this session"; routes/crew-queries.js,
// migration 167). The office is emailed each one with the day attached; this is where it is looked into and
// answered. Resolving needs a note, because the picker is emailed it. A resolved query can be reopened.

export interface CrewQuery {
  id: string
  work_date: string
  person: { id: string; name: string }
  team: string | null
  message: string
  received_at: string
  status: 'open' | 'resolved'
  resolved: { at: string; by: string; resolution: string } | null
  emailed_to: string[]
  email_error: string | null
  pdf_bytes: number
}

type Show = 'open' | 'resolved' | 'all'

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })
const dayLabel = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
const errText = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong.')

export function QueriesPage() {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const linked = params.get('query')
  const [show, setShow] = useState<Show>(linked ? 'all' : 'open')
  const [queries, setQueries] = useState<CrewQuery[] | null>(null)
  const [open, setOpen] = useState(0)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [answering, setAnswering] = useState<string | null>(null)
  const [answer, setAnswer] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [failed, setFailed] = useState<{ at: string; text: string } | null>(null)
  const [told, setTold] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      const r = await apiJson<{ queries: CrewQuery[]; open: number }>(user, `/api/queries?status=${show}`)
      setQueries(r.queries)
      setOpen(r.open)
      setOpenQueries(r.open)
    } catch (err) {
      setLoadError(errText(err))
    }
  }, [user, show])

  useEffect(() => {
    setQueries(null)
    load()
  }, [load])

  // The email's link names one query: bring it into view.
  useEffect(() => {
    if (!linked || !queries) return
    document.getElementById(`query-${linked}`)?.scrollIntoView?.({ block: 'center' })
  }, [linked, queries])

  const replace = (q: CrewQuery) => setQueries((qs) => (qs ? qs.map((x) => (x.id === q.id ? q : x)) : qs))

  const openPdf = async (q: CrewQuery) => {
    setBusy(`pdf-${q.id}`)
    setFailed(null)
    // Opened before the fetch, so a pop-up blocker sees the click.
    const win = window.open('', '_blank')
    try {
      const res = await apiFetch(user, `/api/queries/${q.id}/pdf`)
      if (!res.ok) throw new Error(res.status === 404 ? 'The day for this query could not be found.' : 'The day could not be opened. Try again.')
      const url = URL.createObjectURL(await res.blob())
      if (win) win.location.href = url
      else window.location.assign(url)
    } catch (err) {
      win?.close()
      setFailed({ at: q.id, text: errText(err) })
    } finally {
      setBusy(null)
    }
  }

  const resolve = async (q: CrewQuery) => {
    if (!answer.trim() || busy) return
    setBusy(q.id)
    setFailed(null)
    setTold(null)
    try {
      const r = await apiJson<{ query: CrewQuery; picker: { emailed?: string; email_error?: string } | null }>(user, `/api/queries/${q.id}/resolve`, {
        method: 'POST',
        body: { resolution: answer.trim() },
      })
      replace(r.query)
      setOpen((n) => {
        setOpenQueries(n - 1)
        return Math.max(0, n - 1)
      })
      setAnswering(null)
      setAnswer('')
      setTold(
        r.picker?.emailed
          ? `Resolved. ${q.person.name} was emailed your answer at ${r.picker.emailed}.`
          : r.picker?.email_error
            ? `Resolved, but the email to ${q.person.name} failed (${r.picker.email_error}). Tell them yourself.`
            : `Resolved. ${q.person.name} has no email address, so tell them yourself.`,
      )
    } catch (err) {
      setFailed({ at: q.id, text: errText(err) })
    } finally {
      setBusy(null)
    }
  }

  const reopen = async (q: CrewQuery) => {
    if (busy) return
    setBusy(q.id)
    setFailed(null)
    setTold(null)
    try {
      const r = await apiJson<{ query: CrewQuery }>(user, `/api/queries/${q.id}/reopen`, { method: 'POST' })
      replace(r.query)
      setOpen((n) => {
        setOpenQueries(n + 1)
        return n + 1
      })
    } catch (err) {
      setFailed({ at: q.id, text: errText(err) })
    } finally {
      setBusy(null)
    }
  }

  const choose = (s: Show) => {
    setShow(s)
    setTold(null)
    if (linked) setParams({}, { replace: true })
  }

  return (
    <>
      <PageHeader title="Queries" tabs={<PairTabs label="Pay" tabs={PAY_TABS} current="Queries" />} />
      <PageBody>
        <p className={styles.notice} data-testid="queries-notice">
          A picker raises a query from Dataphyll Crew when a day's numbers look wrong to them. It comes with the day as their phone showed
          it, and the office is emailed a copy. When you resolve one, the picker is emailed your answer.
        </p>

        <div className={styles.filters} role="group" aria-label="Show">
          {(['open', 'resolved', 'all'] as Show[]).map((s) => (
            <button key={s} type="button" className={show === s ? styles.filterOn : styles.filter} aria-pressed={show === s} onClick={() => choose(s)}>
              {s === 'open' ? 'Open' : s === 'resolved' ? 'Resolved' : 'All'}
            </button>
          ))}
          <span className={styles.count} data-testid="queries-open">
            {open === 0 ? 'Nothing open' : open === 1 ? '1 open' : `${open} open`}
          </span>
        </div>

        {told && (
          <p className={styles.told} role="status">
            {told}
          </p>
        )}

        {loadError ? (
          <div className={styles.error} role="alert">
            <p>{loadError}</p>
            {!/permission set/.test(loadError) && (
              <button type="button" onClick={load}>
                Retry
              </button>
            )}
          </div>
        ) : queries === null ? (
          <SkeletonRows rowClass={styles.skeleton} cols={5} rows={3} label="Loading queries" />
        ) : queries.length === 0 ? (
          <div className={styles.empty}>
            <p>{show === 'open' ? 'No open queries.' : show === 'resolved' ? 'No resolved queries yet.' : 'No picker has raised a query yet.'}</p>
          </div>
        ) : (
          <div className={styles.tableCard}>
            <table className={styles.table} data-testid="queries">
              <thead>
                <tr>
                  <th>Picker</th>
                  <th>Day</th>
                  <th>Their message</th>
                  <th>State</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {queries.map((q) => (
                  <React.Fragment key={q.id}>
                    <tr id={`query-${q.id}`} data-testid="query-row" className={q.id === linked ? styles.linked : undefined}>
                      <td>
                        {q.person.name}
                        {q.team && <div className={styles.sub}>{q.team}</div>}
                      </td>
                      <td>
                        {dayLabel(q.work_date)}
                        <div className={styles.sub}>Raised {when(q.received_at)}</div>
                      </td>
                      <td className={styles.message}>
                        {q.message || <span className={styles.sub}>No message</span>}
                        {q.email_error && <div className={styles.warn}>Not every email went: {q.email_error}</div>}
                      </td>
                      <td>
                        {q.resolved ? (
                          <>
                            <span className={`${styles.pill} ${styles.pillGood}`}>Resolved</span>
                            <div className={styles.sub}>
                              by {q.resolved.by}, {when(q.resolved.at)}
                            </div>
                            <div className={styles.resolution}>{q.resolved.resolution}</div>
                          </>
                        ) : (
                          <span className={`${styles.pill} ${styles.pillWarn}`}>Open</span>
                        )}
                      </td>
                      <td className={styles.actions}>
                        <PendingButton className={styles.btn} busy={busy === `pdf-${q.id}`} pending="Opening…" disabled={!!busy} onClick={() => openPdf(q)}>
                          Open the day
                        </PendingButton>
                        {q.status === 'open' ? (
                          <button
                            type="button"
                            className={styles.btnPrimary}
                            disabled={!!busy}
                            aria-expanded={answering === q.id}
                            onClick={() => {
                              setAnswering(answering === q.id ? null : q.id)
                              setAnswer('')
                              setFailed(null)
                            }}
                          >
                            Resolve…
                          </button>
                        ) : (
                          <PendingButton className={styles.btn} busy={busy === q.id} pending="Reopening…" disabled={!!busy} onClick={() => reopen(q)}>
                            Reopen
                          </PendingButton>
                        )}
                        {failed?.at === q.id && answering !== q.id && <Failed text={failed.text} />}
                      </td>
                    </tr>
                    {answering === q.id && (
                      <tr className={styles.answerRow}>
                        <td colSpan={5}>
                          <form
                            className={styles.answer}
                            aria-label={`Resolve ${q.person.name}'s query`}
                            onSubmit={(e) => {
                              e.preventDefault()
                              resolve(q)
                            }}
                          >
                            <label htmlFor={`answer-${q.id}`}>What did you find, or do? {q.person.name} is emailed this.</label>
                            <textarea
                              id={`answer-${q.id}`}
                              value={answer}
                              maxLength={2000}
                              rows={3}
                              autoFocus
                              placeholder="e.g. Two bins were scanned to another tag. Moved them across: your day now shows 42."
                              onChange={(e) => setAnswer(e.target.value)}
                            />
                            <div className={styles.answerFoot}>
                              <button type="button" className={styles.btn} disabled={busy === q.id} onClick={() => setAnswering(null)}>
                                Cancel
                              </button>
                              <PendingButton type="submit" className={styles.btnPrimary} busy={busy === q.id} pending="Resolving…" disabled={!answer.trim() || !!busy}>
                                Resolve and tell {q.person.name}
                              </PendingButton>
                            </div>
                            {failed?.at === q.id && <Failed text={failed.text} />}
                          </form>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageBody>
    </>
  )
}
